import type { FC } from 'react'
import { memo } from 'react'

import { cn } from '@/utils'

import { CueDropzone } from './cue.dnd'
import { useCueDropzone } from './cue.dnd.hooks'

type CueDropIndicatorProps = {
  /** Insert above or below the cue. */
  position: CueDropzone.BEFORE | CueDropzone.AFTER
  cueId: string
  parentId?: string | null
}

/**
 * The dashed line showing where a dropped cue would land.
 *
 * One component for both directions: the zones are mirror images, differing
 * only in which edge of the cue they sit on.
 *
 * `AFTER` is rendered only on the last cue of a group. It is the only way to
 * reach that final slot, since every other zone inserts before its target.
 */
export const CueDropIndicator: FC<CueDropIndicatorProps> = memo(
  function CueDropIndicator({ position, cueId, parentId }) {
    const { setNodeRef, isOver } = useCueDropzone({
      dropzone: position,
      cueId,
      parentId,
    })

    const isBefore = position === CueDropzone.BEFORE

    return (
      <div
        ref={setNodeRef}
        role="none" // a11y: ignore this element
        className={cn(
          'absolute left-0 right-0 z-10',
          isBefore ? 'top-0' : 'bottom-0',
          // Height defines a dropzone area.
          // Meaning the zone, at which pointer intersection will trigger `isOver` event.
          'h-1/3',
          !isOver && 'pointer-events-none',
        )}
      >
        {isOver && (
          <div
            className={cn(
              'absolute right-0 left-0 h-2 border-dashed border-blue-500',
              isBefore ? 'top-1.5 border-t-2' : 'bottom-1.5 border-b-2',
            )}
          />
        )}
      </div>
    )
  },
)
